import { TextAreaTemplate } from '@templates';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { RoutesList } from '@cycle-hangars/routes/Routes';
import { useCycleStore } from '@cycle-hangars/store/store';
import { ErrorMessages } from '@types';
import { parseZodErrors, scrollTop } from '@utils';
import { ZodError } from 'zod';

export const TellUsMore = () => {
  const { updateTellUsMoreText, tellUsMoreText, cycleHangarIssue } =
    useCycleStore();
  const [textInput, setTextInput] = useState(tellUsMoreText);
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const navigate = useNavigate();
  const MAX_LENGTH = 500;
  const mandatoryAnswer = cycleHangarIssue === 'SomethingElse';
  const pageTitle = mandatoryAnswer
    ? 'Tell us about the issue'
    : 'Tell us more about the issue (optional)';
  const description = mandatoryAnswer
    ? 'Add details about the cycle hangar issue.'
    : 'Add any further information that may be useful to know.';
  const errorPath = 'tellUsMore';

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTextInput(event.target.value);
    setErrorMessages([]);
  };

  const buttonNext = {
    onClick: () => {
      setErrorMessages([]);
      try {
        if (mandatoryAnswer && textInput === '') {
          setErrorMessages([
            {
              path: errorPath,
              message: 'Enter details about the issue.',
            },
          ]);
          return;
        }
        if (textInput.length > MAX_LENGTH) {
          setErrorMessages([
            {
              path: errorPath,
              message:
                'Details about the issue must be 500 characters or less.',
            },
          ]);
          return;
        }

        updateTellUsMoreText(textInput);
        navigate(RoutesList.EMAIL_CONFIRMATION_REPORT);
        scrollTop();
      } catch (error) {
        const validationErrors = parseZodErrors(error as ZodError);
        setErrorMessages(
          validationErrors.map((error: ErrorMessages) => ({
            ...error,
            href: error.path,
          })),
        );
      }
    },
  };

  const handleNavigate = () => {
    switch (cycleHangarIssue) {
      case 'BrokenLock':
        navigate(RoutesList.LOCK_CODE_DETAILS);
        break;
      case 'BikeTheft':
        navigate(RoutesList.BIKE_THEFT);
        break;
      case 'DamagedOrVandalism':
        navigate(RoutesList.DAMAGED_OR_VANDALISED_TELL_US_MORE);
        break;
      case 'Cleaning':
        navigate(RoutesList.CLEANING);
        break;
      case 'AbandonedOrUnauthorisedBike':
        navigate(RoutesList.ABANDONED_OR_UNAUTHORISED_DESCRIBE);
        break;
      case 'SomethingElse':
        navigate(RoutesList.ISSUE_SELECTION);
      default:
        return;
    }
  };

  const buttonPrevious = {
    onClick: () => {
      handleNavigate();
      scrollTop();
    },
  };

  return (
    <TextAreaTemplate
      initialTextValue={textInput}
      labelText={pageTitle}
      description={description}
      onChange={handleChange}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
      maxLength={MAX_LENGTH}
      errorPath={errorPath}
      errorMessages={errorMessages}
    />
  );
};
